import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LoginResult, Role, UserView } from '@geoic/shared';
import { api } from '@/lib/api-client.js';

export type AuthState = {
  user: UserView | null;
  /** Boshlang'ich seans tekshiruvi tugadimi. */
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Foydalanuvchi roli sanab o'tilganlardan birimi. */
  can: (...roles: readonly Role[]) => boolean;
};

const AuthContext = createContext<AuthState | null>(null);

/**
 * Seans holati.
 *
 * Ilova ochilganda `/auth/me` chaqiriladi: access token xotirada yo'q, lekin
 * refresh cookie brauzerda qolgan bo'lishi mumkin. API klienti 401 ni ushlab
 * tokenni yangilaydi va so'rovni qaytaradi — foydalanuvchi qayta kirishi
 * shart emas.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserView | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await api.get<{ data: UserView }>('/auth/me');
        if (!cancelled) setUser(response.data);
      } catch {
        // Seans yo'q — bu xato emas, oddiy holat: login sahifasi ko'rsatiladi
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Token butunlay yaroqsiz bo'lganda ilova login sahifasiga qaytadi
    api.setUnauthorizedHandler(() => setUser(null));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post<{ data: LoginResult }>('/auth/login', { email, password });
    api.setAccessToken(response.data.accessToken);
    setUser(response.data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Server javob bermasa ham interfeys chiqarib yuboradi: foydalanuvchi
      // «chiqdim» deb o'ylab, ochiq seansni qoldirib ketmasin.
      api.setAccessToken(null);
      setUser(null);
    }
  }, []);

  const can = useCallback(
    (...roles: readonly Role[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, ready, login, logout, can }),
    [user, ready, login, logout, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth faqat <AuthProvider> ichida ishlaydi');
  return context;
}
